// Which questionnaire a beneficiary fills (spec #86, #92). The admin keeps
// rules of (organization?, age range?, questionnaire); the most specific rule
// that matches wins: organization and age, then organization, then age, then
// the general rule. Overlapping rules are refused when saved, so the choice is
// never ambiguous for one organization. Without a birth date, age rules are
// skipped. A change applies from the next fill: past answers stay as they are.
// Pure; copied identically to mobile and functions/assessment.

export interface AssignmentRule {
  id: string;
  /** null = every organization */
  organizationId?: string | null;
  /** age in whole years, from inclusive; null = no lower bound */
  ageFrom?: number | null;
  /** age in whole years, to exclusive ("under 18" = ageTo 18); null = no upper bound */
  ageTo?: number | null;
  questionnaireId: string;
}

export interface AssignmentSubject {
  organizationIds: string[];
  /** whole years, or null when the birth date is not recorded */
  age: number | null;
}

const hasAge = (r: AssignmentRule) => r.ageFrom != null || r.ageTo != null;

/** 3 organization + age, 2 organization, 1 age, 0 general. */
export function specificity(r: AssignmentRule): number {
  return (r.organizationId ? 2 : 0) + (hasAge(r) ? 1 : 0);
}

function matches(r: AssignmentRule, s: AssignmentSubject): boolean {
  if (r.organizationId && !s.organizationIds.includes(r.organizationId)) return false;
  if (hasAge(r)) {
    if (s.age === null) return false;
    if (r.ageFrom != null && s.age < r.ageFrom) return false;
    if (r.ageTo != null && s.age >= r.ageTo) return false;
  }
  return true;
}

/**
 * The rule that decides this subject's questionnaire, or null when none
 * matches. Rules pointing at an archived questionnaire are ignored. Between
 * equally specific rules from different organizations the first in `rules`
 * order wins, so callers pass them in a stable order (oldest first).
 */
export function resolveAssignment(
  rules: AssignmentRule[],
  subject: AssignmentSubject,
  archivedQuestionnaireIds: string[] = []
): AssignmentRule | null {
  let best: AssignmentRule | null = null;
  for (const r of rules) {
    if (archivedQuestionnaireIds.includes(r.questionnaireId) || !matches(r, subject)) continue;
    if (!best || specificity(r) > specificity(best)) best = r;
  }
  return best;
}

const lo = (r: AssignmentRule) => r.ageFrom ?? -Infinity;
const hi = (r: AssignmentRule) => r.ageTo ?? Infinity;

/**
 * Why `rule` cannot be saved next to `existing`, or null when it can. Two
 * rules overlap when they are for the same organization (or both general) and
 * their age ranges share an age; a rule with no age range covers all ages, so
 * it only overlaps another rule without one.
 */
export function ruleProblem(rule: AssignmentRule, existing: AssignmentRule[]): 'empty_range' | 'overlap' | null {
  if (rule.ageFrom != null && rule.ageTo != null && rule.ageFrom >= rule.ageTo) return 'empty_range';
  for (const other of existing) {
    if (other.id === rule.id) continue;
    if ((other.organizationId || null) !== (rule.organizationId || null)) continue;
    if (hasAge(other) !== hasAge(rule)) continue;
    if (lo(rule) < hi(other) && lo(other) < hi(rule)) return 'overlap';
  }
  return null;
}

/** Whole years at `at`, or null without a birth date. */
export function ageInYears(birthDate: Date | null | undefined, at: Date): number | null {
  if (!birthDate) return null;
  let years = at.getUTCFullYear() - birthDate.getUTCFullYear();
  const beforeBirthday =
    at.getUTCMonth() < birthDate.getUTCMonth() ||
    (at.getUTCMonth() === birthDate.getUTCMonth() && at.getUTCDate() < birthDate.getUTCDate());
  if (beforeBirthday) years -= 1;
  return years;
}
